import { xScale, type Brush } from './plotSurface.ts';

/**
 * The cursor line, the pinned playhead and the staged brush — drawn the same
 * way in every instrument so one glance across a grid of panels reads as one
 * cursor rather than as six that happen to line up.
 *
 * The playhead is dashed accent (the operator put it there); the live cursor
 * is a solid neutral hairline (the pointer is there now). They are only both
 * on screen while the pointer is somewhere other than the pin.
 */
export function CursorOverlay({
  from, to, width, height, at, playhead, brush,
}: {
  from: number;
  to: number;
  width: number;
  height: number;
  at: number | null;
  playhead: number | null;
  brush: Brush | null;
}) {
  const x = xScale(from, to, width);
  const inside = (t: number | null): t is number => t !== null && t >= from && t <= to;
  /* The pin and the pointer land on the same pixel constantly — a click sets
     both — and drawing a dashed amber rule under a solid grey one at the same
     x is a 2px-wide smear that reads as an artefact. Under two pixels apart
     they are one cursor and only the live one is drawn. */
  const apart =
    playhead !== null && at !== null && Math.abs(x(playhead) - x(at)) >= 2;
  return (
    <>
      {brush !== null && (
        <rect
          x={x(brush.from)}
          y={0}
          width={Math.max(1, x(brush.to) - x(brush.from))}
          height={height}
          fill="var(--accent-soft)"
          stroke="var(--accent-line)"
          strokeWidth={1}
          shapeRendering="crispEdges"
        />
      )}
      {inside(playhead) && apart && (
        <line
          x1={x(playhead)} x2={x(playhead)} y1={0} y2={height}
          stroke="var(--accent)" strokeWidth={1} strokeDasharray="2 2"
          shapeRendering="crispEdges"
        />
      )}
      {inside(at) && (
        <line
          x1={x(at)} x2={x(at)} y1={0} y2={height}
          stroke={playhead !== null && !apart ? 'var(--accent)' : 'var(--plot-crosshair)'}
          strokeWidth={1}
          shapeRendering="crispEdges"
        />
      )}
    </>
  );
}
